import { genres, regions, yearsList, ratingRanges } from "@/mocks/movies";

export interface Filters {
  genre: string;
  region: string;
  year: string;
  rating: string;
}

interface FilterSidebarProps {
  filters: Filters;
  onSelect: (field: keyof Filters, value: string) => void;
  onReset: () => void;
  hasActive: boolean;
}

interface GroupProps {
  title: string;
  options: { label: string; value: string }[];
  activeValue: string;
  onSelect: (value: string) => void;
}

function FilterGroup({ title, options, activeValue, onSelect }: GroupProps) {
  return (
    <div className="border-b border-background-200 py-4 last:border-b-0">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-900">
        <span className="flex h-4 w-4 items-center justify-center text-primary-500">
          <i className="ri-filter-3-line text-sm"></i>
        </span>
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = activeValue === opt.value;
          return (
            <button
              key={opt.value || "all"}
              type="button"
              onClick={() => onSelect(opt.value)}
              className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterSidebar({
  filters,
  onSelect,
  onReset,
  hasActive,
}: FilterSidebarProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
        {hasActive && (
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-secondary-600 transition-colors hover:text-secondary-700"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-refresh-line text-sm"></i>
            </span>
            重置
          </button>
        )}
      </div>

      <FilterGroup
        title="类型"
        activeValue={filters.genre}
        onSelect={(v) => onSelect("genre", v)}
        options={[{ label: "全部", value: "" }, ...genres.map((g) => ({ label: g, value: g }))]}
      />
      <FilterGroup
        title="地区"
        activeValue={filters.region}
        onSelect={(v) => onSelect("region", v)}
        options={[{ label: "全部", value: "" }, ...regions.map((r) => ({ label: r, value: r }))]}
      />
      <FilterGroup
        title="年份"
        activeValue={filters.year}
        onSelect={(v) => onSelect("year", v)}
        options={[
          { label: "全部", value: "" },
          ...yearsList.map((y) => ({ label: `${y}`, value: `${y}` })),
        ]}
      />
      <FilterGroup
        title="评分区间"
        activeValue={filters.rating}
        onSelect={(v) => onSelect("rating", v)}
        options={[
          { label: "全部", value: "" },
          ...ratingRanges.map((r) => ({ label: r.label, value: r.label })),
        ]}
      />
    </div>
  );
}